import {
  useCreateResidentMutation,
  useUpdateResidentMutation,
  useUploadResidentProfileImageMutation,
} from "app/services/api/residentsApi";
import { AddResidentBioForm } from "app/services/api/types/residents/models/AddResidentBioForm";
import { CreateResidentRequest } from "app/services/api/types/residents/requests/CreateResidentRequest";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useResidentContext } from "../provider/ResidentProvider";
import { handleBackendError } from "ui/utils/Errors";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import * as mime from "react-native-mime-types";

export const useHandleResidentBio = () => {
  const { resident, setResidentDetails } = useResidentContext();
  const { showNewToast, showErrorToast } = useDisplayToast();

  const [triggerCreateResident] = useCreateResidentMutation();
  const [triggerUploadResidentProfileImage] =
    useUploadResidentProfileImageMutation();
  const [triggerUpdateResident] = useUpdateResidentMutation();

  const [imageUrl, setImageUrl] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { isSubmitting, isDirty },
  } = useForm<AddResidentBioForm>({
    defaultValues: {
      relatives: [
        {
          first_name: "",
          last_name: "",
          relationship: "",
          contact_number: "",
          address: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "relatives",
  });

  const outsidePassStatus = useWatch({
    control,
    name: "outside_pass_status",
  });

  const maritalStatus = useWatch({
    control,
    name: "marital_status",
  });

  const shouldShowOutsidePassDetails = useMemo(
    () => outsidePassStatus === "other",
    [outsidePassStatus],
  );
  const shouldShowMaritalDetails = useMemo(
    () => maritalStatus === "other",
    [maritalStatus],
  );

  useEffect(() => {
    if (outsidePassStatus !== "other") {
      setValue("outside_pass_status_details", "");
    }
  }, [outsidePassStatus, setValue]);

  const { profile_image: profileImage } = useWatch({ control });
  useEffect(() => {
    if (profileImage) {
      try {
        setImageUrl(profileImage.uri);
      } catch {}
    } else {
      setImageUrl(undefined);
      clearValidationErrors();
    }
  }, [profileImage]);

  useEffect(() => {
    if (resident) {
      reset({
        ...resident,
        nationality_id: resident.nationality?.id,
        location_id: resident.location?.id,
        profile_image: resident.profile_picture
          ? {
              name: resident.profile_picture.file_name,
              uri: resident.profile_picture.url,
              mimeType:
                mime.lookup(resident.profile_picture.file_name) || "image/jpeg",
            }
          : undefined,
      });
    }
  }, [resident, reset]);

  const clearValidationErrors = () => {
    setErrorMessage(null);
    setValue("profile_image", undefined);
  };

  const uploadProfileImage = async (image: File, residentId: number) => {
    if (image) {
      const formData = new FormData();
      formData.append("profile_image", image);

      await triggerUploadResidentProfileImage({
        residentId,
        body: formData,
      }).unwrap();
    }
  };

  const updateOrCreateResident = async (
    id: number | undefined,
    data: CreateResidentRequest,
  ) => {
    const isEditing = !!resident?.id;

    if (isEditing) {
      await triggerUpdateResident({
        id: resident.id,
        body: data,
      }).unwrap();
      return resident.id;
    } else {
      const residentResponse = await triggerCreateResident(data).unwrap();
      setResidentDetails(residentResponse);
      return residentResponse.id;
    }
  };

  const submitForm = () => {
    return new Promise<boolean>((resolve) => {
      if (isSubmitting) {
        resolve(false);
        return;
      }
      handleSubmit(
        async (data: AddResidentBioForm) => {
          try {
            if (!isDirty) {
              resolve(true);
              return;
            }
            const isEditing = !!resident?.id;

            const newResidentId = await updateOrCreateResident(
              resident?.id,
              data,
            );

            if (data.profile_image?.file)
              await uploadProfileImage(data.profile_image.file, newResidentId);

            reset({ ...data });

            showNewToast({
              title: isEditing ? "Resident Updated" : "Resident Created",
              message: isEditing
                ? "Successfully updated the resident details."
                : "Successfully created a new resident.",
              type: "success",
            });

            resolve(true);
          } catch (e) {
            const result = handleBackendError(e, setError);
            showErrorToast(e);
            resolve(result);
          }
        },
        (errors) => {
          console.error("Form errors:", errors);
          resolve(false);
        },
      )();
    });
  };

  const appendNextOfKin = () => {
    append({
      first_name: "",
      last_name: "",
      relationship: "",
      contact_number: "",
      address: "",
    });
  };

  return {
    control,
    fields,
    appendNextOfKin,
    remove,
    submitForm,
    reset,
    imageUrl,
    errorMessage,
    setValue,
    shouldShowOutsidePassDetails,
    shouldShowMaritalDetails,
  };
};
